/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useState } from 'react';

import { useRetracked } from 'js/lib/retracked';

import { Button, Grid, TextField } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { LinkIcon, SuccessFilledIcon } from '@coursera/cds-icons';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import { EVENTS } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/events';
import { getProfileUrl } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/profileSlugUtils';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/account-profile';

const styles = {
  removeInheritedStyles: css`
    input {
      margin-bottom: 0;
      border: 0;
      box-shadow: none;
      background: transparent;
    }

    input[aria-invalid='true']:invalid:focus {
      box-shadow: none;
    }
  `,
  withPaddingRight: (theme: Theme) => css`
    padding-right: ${theme.spacing(16)};
  `,
};

type CopyProfileLinkProps = {
  currentSlug?: string;
};

const CopyProfileLink: React.VFC<CopyProfileLinkProps> = ({ currentSlug }) => {
  const [hasError, setHasError] = useState<boolean>(false);
  const [isLinkCopiedToClipboard, setIsLinkCopiedToClipboard] = useState<boolean>(false);
  const { profileState = {}, sharedEventingV3Data } = useContext(ProfileContext);
  const { externalId } = profileState;

  const retrackedTrack = useRetracked();
  const track = useTracker();

  const visibilityTrackingRef: React.MutableRefObject<HTMLButtonElement | null> = useVisibilityTracker('view_button', {
    button: {
      name: EVENTS.copyShareLink,
    },
    ...sharedEventingV3Data,
  });

  if (!externalId) return null;

  const trackClick = () => {
    retrackedTrack({
      trackingName: EVENTS.copyShareLink,
      trackingData: {},
      action: 'click',
    });

    track('click_button', {
      button: {
        name: EVENTS.copyShareLink,
      },
      ...sharedEventingV3Data,
    });
  };

  const onCopy = () => {
    navigator.clipboard
      .writeText(getProfileUrl(profileState, currentSlug))
      .then(() => {
        setIsLinkCopiedToClipboard(true);
        setHasError(false);

        // Reset the copied button state
        setTimeout(() => setIsLinkCopiedToClipboard(false), 3000);
      })
      .catch(() => setHasError(true));
  };

  const handleCopyBtnClick = () => {
    trackClick();
    onCopy();
  };

  return (
    <Grid container item direction="row" xs={12} alignItems="flex-end">
      <Grid item xs={9} css={styles.withPaddingRight}>
        <TextField
          css={styles.removeInheritedStyles}
          fullWidth
          readOnly
          prefix={<LinkIcon />}
          label={_t('Profile link')}
          supportText={_t(
            'Only someone with a link to your profile can view it (it won’t appear as a result on search engines).'
          )}
          validationStatus={hasError ? 'error' : undefined}
          validationLabel={_t('Something went wrong. Please try again')}
          value={getProfileUrl(profileState, currentSlug)}
        />
      </Grid>
      <Grid item xs={3}>
        <TrackedDiv
          trackClicks={false}
          trackingName={EVENTS.copyShareLink}
          requireFullyVisible={false}
          withVisibilityTracking={true}
        >
          <Button
            ref={visibilityTrackingRef}
            fullWidth
            variant="secondary"
            iconPosition="before"
            icon={isLinkCopiedToClipboard ? <SuccessFilledIcon /> : undefined}
            onClick={handleCopyBtnClick}
          >
            {isLinkCopiedToClipboard ? _t('Copied') : _t('Copy link')}
          </Button>
        </TrackedDiv>
      </Grid>
    </Grid>
  );
};

export default CopyProfileLink;
